<template>
  <div
    v-if="$helper.isAmpPage($route.name) && showOnAmp"
    class="activator__wrapper--amp"
  >
    <div class="activator__hover-content">
      <slot name="activator-content" />
    </div>

    <div class="popup__container--amp">
      <slot name="popup-header" />
      <slot name="popup-content" />
      <i class="popup__beak-icon top" />
    </div>
  </div>

  <div v-else v-on-clickaway="onClickAway" class="activator__wrapper">
    <div
      ref="hoverElement"
      v-focusable
      class="activator__hover-content"
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
      @click="onClick"
      @focus="onMouseEnter"
      @blur="onMouseLeave"
    >
      <slot name="activator-content" />
    </div>

    <client-only>
      <Transition name="fade">
        <div
          v-if="isPopupVisible"
          v-observe-visibility="hoverVisibilityOptions"
          class="popup__wrapper"
          :style="popupStyles"
        >
          <div class="popup__container">
            <slot name="popup-header" />
            <slot name="popup-content" />
          </div>
          <a-icon
            v-if="showCloseIcon"
            :width="12"
            :height="12"
            class="popup__icon"
            is-button
            :icon="$options.consts.ICON.CROSS"
            @click="hidePopup"
          />
          <i
            v-if="showBeak"
            :class="beakDynamicClass"
            class="popup__beak-icon"
          />
        </div>
      </Transition>
    </client-only>
  </div>
</template>

<script>
import { propValidator, PROP_TYPES } from '@/utils/validators'
import AIcon, { ICON } from 'shared/AIcon'
import {
  LAYOUT_AREA_SIZE_FN,
  LAYOUT_SETTINGS,
  DESKTOP_POPUP_SETTINGS,
  MOBILE_POPUP_SETTINGS
} from '~/components/_shared/APopup/enums'
import { hydrationHelpers } from '@/utils/mixins/hydrationHelpers'
import { OBSERVER_CALLBACK_DEFAULT_THROTTLE } from '~/utils/enums/mutation-observer'
import { directive as onClickaway } from 'vue-clickaway'
import { LAYOUT_ORDERING } from './enums'

export default {
  name: 'APopup',
  mixins: [hydrationHelpers],
  directives: { onClickaway },
  components: { AIcon },
  consts: { ICON },
  props: {
    layoutOrdering: propValidator(
      [PROP_TYPES.ARRAY],
      false,
      () => LAYOUT_ORDERING
    ),
    popupSettingsByBreakpoint: propValidator([PROP_TYPES.OBJECT], false),
    topSpaceOffset: propValidator([PROP_TYPES.NUMBER], false, 0),
    bottomSpaceOffset: propValidator([PROP_TYPES.NUMBER], false, 0),
    showCloseIcon: propValidator([PROP_TYPES.BOOLEAN], false),
    showBeak: propValidator([PROP_TYPES.BOOLEAN], false),
    persistent: propValidator([PROP_TYPES.BOOLEAN], false, false),
    showOnAmp: propValidator([PROP_TYPES.BOOLEAN], false, false),
    hoverable: propValidator([PROP_TYPES.BOOLEAN], false, true),
    openOnMount: propValidator([PROP_TYPES.BOOLEAN], false, false)
  },
  data() {
    return {
      isPopupVisible: false,
      popupActivatorProps: {},
      hoverElementPositionData: {
        leftSpace: 0,
        rightSpace: 0,
        topSpace: 0,
        bottomSpace: 0
      },
      POPUP_SETTINGS_BY_BREAKPOINT: {
        [this.$breakpoint.aboveDesktopXl]: DESKTOP_POPUP_SETTINGS,
        [this.$breakpoint.mobile]: MOBILE_POPUP_SETTINGS
      },
      popupPosition: {
        top: null,
        bottom: null,
        left: null,
        right: null
      },
      hoverVisibilityOptions: {
        callback: visibility => {
          if (visibility) return

          if (!this.persistent) {
            this.hidePopup()
          }
        },
        throttle: OBSERVER_CALLBACK_DEFAULT_THROTTLE
      }
    }
  },
  computed: {
    popupStyles() {
      const positionStyles = {
        ...(this.popupPosition.top != null
          ? { top: `${this.popupPosition.top}px` }
          : {}),
        ...(this.popupPosition.bottom != null
          ? { bottom: `${this.popupPosition.bottom}px` }
          : {}),
        ...(this.popupPosition.left != null
          ? { left: `${this.popupPosition.left}px` }
          : {}),
        ...(this.popupPosition.right != null
          ? { right: `${this.popupPosition.right}px` }
          : {})
      }

      const positionSize = {
        ...(this.popupSettings.modalWidth != null
          ? { width: `${this.popupSettings.modalWidth}px` }
          : {}),
        ...(this.popupSettings.modalHeight != null
          ? { height: `${this.popupSettings.modalHeight}px` }
          : {})
      }

      return {
        ...positionStyles,
        ...positionSize
      }
    },
    beakDynamicClass() {
      return {
        top: this.popupPosition.top != null,
        bottom: this.popupPosition.bottom != null
      }
    },
    popupSettings() {
      if (!process.client) return

      return this.$_hydrationHelpers_getValueByBreakpoint(
        this.popupSettingsByBreakpoint || this.POPUP_SETTINGS_BY_BREAKPOINT
      )
    }
  },
  watch: {
    $_hydrationHelpers_windowWidth: {
      handler() {
        if (!this.persistent) {
          this.hidePopup()
        }
      }
    }
  },
  methods: {
    onMouseEnter() {
      if (this.$_hydrationHelpers_isLayoutMobile || !this.hoverable) {
        return
      }

      this.showPopup()
    },
    onMouseLeave() {
      if (
        this.$_hydrationHelpers_isLayoutMobile ||
        !this.hoverable ||
        this.persistent
      ) {
        return
      }

      this.hidePopup()
    },
    onClick() {
      if (!this.$_hydrationHelpers_isLayoutMobile || !this.hoverable) {
        return
      }

      this.showPopup()
    },
    onClickAway() {
      if (
        !this.$_hydrationHelpers_isLayoutMobile ||
        !this.hoverable ||
        this.persistent
      ) {
        return
      }

      this.hidePopup()
    },
    showPopup() {
      this.calculatePosition()
      this.isPopupVisible = true
    },
    hidePopup() {
      this.isPopupVisible = false
    },
    calculatePosition() {
      this.setHoverElementPositionData()
      this.popupActivatorProps = this.getUpdatedActivatorDimensions()

      const currentLayout = this.getLayoutByPosition({
        ...this.popupSettings,
        ...this.hoverElementPositionData,
        ...(this.popupActivatorProps || {})
      })
      this.setPopupPositionByLayout(currentLayout)
    },
    setHoverElementPositionData() {
      if (!this.$refs.hoverElement) return

      const {
        top,
        right,
        bottom,
        left
      } = this.$refs.hoverElement.getBoundingClientRect()

      this.hoverElementPositionData = {
        leftSpace: left,
        topSpace: top - this.topSpaceOffset,
        rightSpace:
          this.$_hydrationHelpers_windowWidth -
          right -
          this.$helper.getScrollBarWidth(),
        bottomSpace:
          this.$_hydrationHelpers_windowHeight - bottom - this.bottomSpaceOffset
      }
    },
    getUpdatedActivatorDimensions() {
      if (!process.client) return null

      const { clientWidth, clientHeight } = this.$refs.hoverElement

      return {
        popupActivatorWidth: clientWidth,
        popupActivatorHeight: clientHeight
      }
    },
    getLayoutByPosition(positionData) {
      const layoutKey = this.layoutOrdering.find(layout => {
        const { validationFn } = LAYOUT_SETTINGS[layout]

        return validationFn(positionData)
      })

      return layoutKey || this.getLayoutByMaxAreaSize(positionData)
    },
    getLayoutByMaxAreaSize(positionData) {
      const layoutOrderingShallowCopy = [...this.layoutOrdering]

      return layoutOrderingShallowCopy.sort((a, b) => {
        const aAreaSize = LAYOUT_AREA_SIZE_FN[a](positionData)
        const bAreaSize = LAYOUT_AREA_SIZE_FN[b](positionData)

        if (aAreaSize > bAreaSize) return -1

        if (bAreaSize > aAreaSize) return 1

        return 0
      })[0]
    },
    setPopupPositionByLayout(layout) {
      if (!layout || !this.popupSettings) return

      const layoutSettings = LAYOUT_SETTINGS[layout]
      const { top, bottom, left, right } = layoutSettings.getModalPositionFn({
        ...this.popupSettings,
        ...this.popupActivatorProps
      })

      this.popupPosition = {
        top: top,
        bottom: bottom,
        left: left,
        right: right
      }
    },
    async initNotificationPopup() {
      await this.$nextTick()
      setTimeout(this.showPopup, 500)
    }
  },
  mounted() {
    if (this.openOnMount) {
      this.initNotificationPopup()
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'assets/scss/global/mixins/_figures.scss';
.activator__wrapper--amp {
  position: relative;
  z-index: $z-index-popup;

  .popup__container--amp {
    display: none;
  }

  &:hover {
    .popup__container--amp {
      display: block;
    }
  }
}

.activator__wrapper {
  position: relative;
  z-index: $z-index-popup;

  .popup__container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
}

.activator__hover-content {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  cursor: default;
}

.popup__wrapper {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  position: absolute;
  padding: 12px;
  border-radius: 4px;
  background: $c--gray-darkest;
  color: $c--white;
  z-index: $z-index-popup;

  .popup__header {
    height: 20px;
    display: flex;
    justify-content: flex-end;
  }
}

.popup__icon {
  fill: $c--white;
  width: auto;
  margin-left: 8px;
}

.popup__beak-icon {
  position: absolute;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  left: 50%;
  transform: translateX(-50%);
  z-index: $z-index-popup - 1;

  &.top {
    border-bottom: 10px solid $c--gray-darkest;
    top: -10px;
  }

  &.bottom {
    border-top: 10px solid $c--gray-darkest;
    bottom: -10px;
  }
}

.fade-enter-active {
  animation: fade-in 0.3s;
}

.fade-leave-active {
  animation: fade-in 0.3s reverse;
}

@keyframes fade-in {
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
}
</style>
